import React, { useEffect, useState } from 'react';

/**
 * 슬롯 위임 picker — invitation 어댑터 + 마켓 InvitationCreateModal 공용.
 *
 * 동작:
 * - to family 의 슬롯 목록을 fetch (`/mofamily/{hash}/slots`)
 * - 각 슬롯에 D(독점) / S(공유) / 해제 토글
 * - 독점 클릭 시 위임 체인 공유 위임 여부 검사 (`/check-slot-delegation-chain`) → 충돌 시 자동 공유로 강제
 * - 권한 없는 슬롯 (이미 다른 family 에 위임됨) 은 비활성 표시
 *
 * 외부에 onChange 로 (delegation[], slotNumbers[]) 통지.
 */

export type SlotState = 'none' | 'dedicated' | 'shared';

export interface SlotMeta {
 slot_number: number;
 has_authority: boolean;
}

export interface SlotPickerProps {
 toFamilyHash: string;
 selectedFamilyHash: string;
 initial: { dedicated: number[]; shared: number[] };
 onChange: (delegation: SlotState[], slotNumbers: number[]) => void;
 disabled: boolean;
 /** initial 이 비어 있으면 첫 위임 가능 슬롯을 독점으로 미리 선택(신규 초대 기본값).
  *  무위임 초대는 "관계만 있고 배포가 안 흐르는" 흔한 함정이라 켜진 상태로 시작한다. */
 defaultFirstDedicated?: boolean;
}

const SlotPicker: React.FC<SlotPickerProps> = ({
 toFamilyHash, selectedFamilyHash, initial, onChange, disabled, defaultFirstDedicated,
}) => {
 const [slotMetas, setSlotMetas] = useState<SlotMeta[]>([]);
 const [delegation, setDelegation] = useState<SlotState[]>([]);
 // 영상 박스 구동 슬롯 — 기본 선택에서 제외, 명시 선택 시 확인(위임 성립 = 박스 바인딩 해제).
 const [boxBoundSlots, setBoxBoundSlots] = useState<number[]>([]);

 useEffect(() => {
  let alive = true;
  Promise.all([
   fetch(`/mofamily/${toFamilyHash}/slots`, { credentials: 'include' }).then(r => r.json()),
   fetch(`/videobox/bindings/${toFamilyHash}`, { credentials: 'include' })
    .then(r => (r.ok ? r.json() : { data: [] }))
    .catch(() => ({ data: [] })),
  ])
   .then(([j, bj]) => {
    if (!alive) return;
    const slots: any[] = j.data ?? [];
    const boxBound: number[] = (bj.data || []).map((b: any) => b.slot_number);
    const metas: SlotMeta[] = slots.map(s => ({
     slot_number: s.slot_number,
     has_authority: !!s.has_authority,
    }));
    const initDel: SlotState[] = metas.map(m => {
     if (initial.dedicated.includes(m.slot_number)) return 'dedicated';
     if (initial.shared.includes(m.slot_number)) return 'shared';
     return 'none';
    });
    // 신규 초대 기본값: 아무 선택이 없으면 첫 위임 가능 슬롯을 독점으로.
    // 박스 구동 슬롯은 건너뛴다(집으면 위임 성립 시 박스 편성이 조용히 풀린다).
    if (defaultFirstDedicated && !initDel.some(s => s !== 'none')) {
     const firstIdx = metas.findIndex(m => m.has_authority && !boxBound.includes(m.slot_number));
     if (firstIdx >= 0) initDel[firstIdx] = 'dedicated';
    }
    setBoxBoundSlots(boxBound);
    setSlotMetas(metas);
    setDelegation(initDel);
    onChange(initDel, metas.map(m => m.slot_number));
   })
   .catch(() => { /* 빈 폼 fallback */ });
  return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [toFamilyHash]);

 const handleClick = async (idx: number) => {
  if (disabled) return;
  const meta = slotMetas[idx];
  if (!meta?.has_authority) return;
  const cur = delegation[idx];
  const slotNumber = meta.slot_number;
  let next: SlotState = cur;
  if (cur === 'none') {
   // 박스 구동 슬롯의 명시 선택 — 위임 성립 시 박스 편성 해제를 확인받는다.
   if (boxBoundSlots.includes(slotNumber)
     && !window.confirm(`슬롯 ${slotNumber}은(는) 영상 박스가 사용 중입니다. 위임이 성립되면 이 슬롯의 박스 편성이 해제됩니다. 계속할까요?`)) {
    return;
   }
   try {
    const r = await fetch(`/mofamily/${selectedFamilyHash}/check-slot-delegation-chain`, {
     method: 'POST',
     headers: { 'Content-Type': 'application/json' },
     credentials: 'include',
     body: JSON.stringify({ slotNumbers: [slotNumber] }),
    });
    const j = await r.json();
    const info = j?.data?.[slotNumber];
    if (info?.hasSharedInChain) {
     alert(`슬롯 ${slotNumber}번은 위임 체인에서 공유 위임이 있어 독점으로 위임할 수 없습니다.`);
     next = 'shared';
    } else {
     next = 'dedicated';
    }
   } catch {
    next = 'dedicated';
   }
  } else if (cur === 'dedicated') {
   next = 'shared';
  } else {
   next = 'none';
  }
  const nextDel = delegation.map((s, i) => (i === idx ? next : s));
  setDelegation(nextDel);
  onChange(nextDel, slotMetas.map(m => m.slot_number));
 };

 if (slotMetas.length === 0) {
  return <div className="text-xs text-gray-500">슬롯 정보를 불러오는 중...</div>;
 }

 return (
  <div>
   <div className="flex flex-wrap gap-1">
    {slotMetas.map((m, i) => {
     const s = delegation[i] ?? 'none';
     const noAuth = !m.has_authority;
     return (
      <button
       key={m.slot_number}
       type="button"
       onClick={() => handleClick(i)}
       disabled={disabled || noAuth}
       className={`w-8 h-5 border transition-colors text-xs ${
        noAuth
         ? 'bg-gray-200 border-gray-300 cursor-not-allowed opacity-50'
         : s === 'none'
         ? 'bg-white border-gray-300 hover:border-gray-400'
         : s === 'dedicated'
         ? 'bg-green-500 border-green-600 text-white'
         : 'bg-blue-500 border-blue-600 text-white'
       }`}
       title={
        noAuth ? '권한 없음' :
        s === 'none' ? '클릭: 독점 위임' :
        s === 'dedicated' ? '클릭: 공유 위임' : '클릭: 위임 해제'
       }
      >
       {noAuth ? '×' : s === 'none' ? m.slot_number : s === 'dedicated' ? 'D' : 'S'}
      </button>
     );
    })}
   </div>
   <div className="mt-2 text-xs text-gray-600 flex items-center gap-3">
    <span><span className="inline-block w-3 h-3 bg-green-500 align-middle mr-1"></span>독점</span>
    <span><span className="inline-block w-3 h-3 bg-blue-500 align-middle mr-1"></span>공유</span>
   </div>
  </div>
 );
};

export default SlotPicker;
